import React, { useState, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, CircularProgress, Snackbar, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useDeficiencies } from '../../../Pages/Deficiencies/DeficiencyContext';
import ImageAnnotatorModal from '../ImageAnnotator/ImageAnnotatorModal';
import ImageCarouselModal from '../ImageCarousel/ImageCarouselModal';
import {
    DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE,
    getJpegFilename,
    MAX_DEFICIENCY_IMAGE_BYTES,
} from '../ImageAnnotator/imageAnnotationUtils';

// Accepted image MIME types. HEIC/HEIF are accepted here because they are converted to JPEG
// on the client before upload.
const ACCEPTED_IMAGE_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/heic',
    'image/heif',
];

/**
 * Returns true if the selected file looks like a supported image, based on its MIME type or,
 * when the browser does not supply one, a HEIC/HEIF extension. Final validation happens
 * server-side; this is the fast, friendly first check before we open the annotator.
 */
const isSupportedImageFile = (file) => {
    const type = (file.type || '').toLowerCase();
    if (type && ACCEPTED_IMAGE_TYPES.includes(type)) {
        return true;
    }
    // Some browsers report an empty type for HEIC/HEIF; fall back to the extension.
    // Note: we intentionally do NOT accept any "image/*" type here — SVG/TIFF/AVIF are not
    // supported by the backend validator, so allowing them would only fail later. Keeping this
    // strict matches the "Please upload a JPG or PNG image" message and the server allow-list.
    const name = (file.name || '').toLowerCase();
    return name.endsWith('.heic') || name.endsWith('.heif');
};

/**
 * Translates an upload failure into a short, non-technical message for field users.
 * Prefers the server's friendly message (validation/size errors), then falls back by HTTP status,
 * and finally to a generic connectivity message for network failures.
 */
const getUploadErrorMessage = (error) => {
    const status = error?.response?.status;
    const serverMessage = error?.response?.data?.message;

    if (status === 400 || status === 413) {
        return serverMessage || 'That image could not be uploaded. Please choose another photo.';
    }
    if (status === 403) {
        return 'You are not allowed to upload images here. Please log in again.';
    }
    if (status === 404) {
        return 'This deficiency could not be found. Please refresh and try again.';
    }
    if (!error?.response) {
        return 'Upload failed — please check your connection and try again.';
    }
    return 'Upload failed. Please try again.';
};

/**
 * Component for displaying and editing images in the deficiency sidebar.
 * Uses forwardRef to expose methods to the parent component for handling delayed image deletion.
 *
 * This component implements a "mark for deletion" pattern where:
 * 1. Images are visually removed when the delete button is clicked
 * 2. The actual API deletion is only performed when the parent calls Save
 * 3. Deletion can be canceled by the parent component if needed
 *
 * @param {Object} props - Component props
 * @param {Array} props.images - Array of DeficiencyImageDTO objects
 * @param {string} props.imageType - Gallery section imageType (e.g. "Before" or "After")
 * @param {boolean} props.isEditing - Whether edit mode is active
 * @param {Function} props.onChange - Callback when images are changed
 * @param {string} props.deficiencyId - The ID of the current deficiency
 * @param {Function} props.onUpload - Callback for image upload handling that preserves edits
 * @param {React.Ref} ref - Forwarded ref that exposes methods to the parent component
 * @param {boolean} props.readOnly - Whether the component is in read-only mode
 * @returns {React.ReactElement} Image gallery component
 */
const ImageGallery = forwardRef(
    (
        {
            images = [],
            imageType,
            isEditing,
            onChange,
            onUpload,
            readOnly = false,
            deficiencyId,
            onDeleteImage,
        },
        ref
    ) => {
        const { getImageUrl } = useDeficiencies();
        const [selectedImageIndex, setSelectedImageIndex] = useState(null);
        const [isUploading, setIsUploading] = useState(false);

        // User-visible upload feedback (validation errors, failures, and success confirmation).
        const [notification, setNotification] = useState({
            open: false,
            message: '',
            severity: 'info',
        });

        const showNotification = (message, severity = 'info') => {
            setNotification({ open: true, message, severity });
        };

        const handleCloseNotification = (_event, reason) => {
            if (reason === 'clickaway') return;
            setNotification((prev) => ({ ...prev, open: false }));
        };

        // Track images that are marked for deletion but not yet actually deleted from the API
        const [imagesToDelete, setImagesToDelete] = useState([]);

        // Annotation state
        const [annotatorOpen, setAnnotatorOpen] = useState(false);
        const [currentAnnotatingImageUrl, setCurrentAnnotatingImageUrl] = useState(null);
        const [currentFileInfo, setCurrentFileInfo] = useState(null); // Store file info during annotation
        const [editingImageInfo, setEditingImageInfo] = useState(null); // Store info for editing existing image

        // Filter out images that are marked for deletion in the UI
        const displayedImages = images.filter(
            (img) => !imagesToDelete.includes(img.deficiencyImageId)
        );

        /**
         * Utility function to convert dataURL to File object
         * @param {string} dataUrl - The data URL from canvas.toDataURL()
         * @param {string} filename - The filename for the new file
         * @returns {File} - File object ready for upload
         */
        const dataURLtoFile = (dataUrl, filename) => {
            if (!dataUrl || typeof dataUrl !== 'string') {
                throw new Error('Invalid dataUrl provided to dataURLtoFile');
            }

            const arr = dataUrl.split(',');
            if (arr.length < 2) {
                throw new Error('Invalid dataUrl format');
            }

            const mimeMatch = arr[0].match(/:(.*?);/);
            if (!mimeMatch) {
                throw new Error('Could not extract MIME type from dataUrl');
            }

            const mime = mimeMatch[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], filename, { type: mime });
        };

        const handleImageUpload = async (event) => {
            if (!event.target.files || event.target.files.length === 0 || readOnly) {
                return;
            }

            try {
                // Set loading state
                setIsUploading(true);

                const inputFiles = Array.from(event.target.files);

                // Validate type and size BEFORE opening the annotator so bad files are rejected
                // immediately with a clear message instead of failing later (or opening the
                // annotator over a blank canvas for an undecodable file).
                const invalidType = inputFiles.find((file) => !isSupportedImageFile(file));
                if (invalidType) {
                    showNotification('Please upload a JPG or PNG image.', 'error');
                    setIsUploading(false);
                    event.target.value = '';
                    return;
                }

                const tooLarge = inputFiles.find((file) => file.size > MAX_DEFICIENCY_IMAGE_BYTES);
                if (tooLarge) {
                    showNotification(DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE, 'error');
                    setIsUploading(false);
                    event.target.value = '';
                    return;
                }

                // Convert HEIC/HEIF images to JPEG on the client before upload
                const convertedFiles = await Promise.all(
                    inputFiles.map(async (file) => {
                        const lowerName = (file.name || '').toLowerCase();
                        const lowerType = (file.type || '').toLowerCase();
                        const isHeicOrHeif =
                            lowerName.endsWith('.heic') ||
                            lowerName.endsWith('.heif') ||
                            lowerType.includes('heic') ||
                            lowerType.includes('heif');

                        if (isHeicOrHeif) {
                            try {
                                const { default: heic2any } = await import('heic2any');
                                const jpegBlob = await heic2any({
                                    blob: file,
                                    toType: 'image/jpeg',
                                    quality: 0.9,
                                });
                                const newName =
                                    lowerName.replace(/\.(heic|heif)$/i, '.jpg') || 'image.jpg';
                                return new File([jpegBlob], newName, { type: 'image/jpeg' });
                            } catch (e) {
                                console.warn('HEIC/HEIF conversion failed; blocking upload', e);
                                return null;
                            }
                        }
                        return file;
                    })
                );

                // Filter out failed conversions
                const successfulFiles = convertedFiles.filter(Boolean);
                if (successfulFiles.length === 0) {
                    showNotification(
                        'This photo could not be processed. Please upload a JPG or PNG image.',
                        'error'
                    );
                    setIsUploading(false);
                    return;
                }

                // Sanitize filenames
                const uploadedFiles = successfulFiles.map((file) => {
                    if (/[\s+,()&%$#@!^*]/.test(file.name)) {
                        const sanitizedName = file.name.replace(/[\s+,()&%$#@!^*]/g, '_');
                        return new File([file], sanitizedName, { type: file.type });
                    }
                    return file;
                });

                // Open annotator for the first file
                if (uploadedFiles.length > 0) {
                    const fileToAnnotate = uploadedFiles[0];
                    const imageUrl = URL.createObjectURL(fileToAnnotate);

                    // Store file info for later
                    setCurrentFileInfo({
                        file: fileToAnnotate,
                        allFiles: uploadedFiles,
                    });

                    // Open annotator
                    setCurrentAnnotatingImageUrl(imageUrl);
                    setAnnotatorOpen(true);
                } else {
                    setIsUploading(false);
                }

                // Clear the file input so the same file can be selected again
                event.target.value = '';
            } catch (error) {
                console.error(`Error uploading ${imageType} images:`, error);
                showNotification(
                    'Something went wrong preparing your photo. Please try again.',
                    'error'
                );
                setIsUploading(false);
            }
        };

        /**
         * Handle saving annotated image
         * @param {string} dataUrl - The annotated image as dataURL
         */
        const handleAnnotatorSave = async (dataUrl, { hasAnnotations = true } = {}) => {
            try {
                // Check if we're editing an existing image
                if (editingImageInfo) {
                    if (!hasAnnotations) {
                        setAnnotatorOpen(false);
                        setCurrentAnnotatingImageUrl(null);
                        setEditingImageInfo(null);
                        return;
                    }

                    // Convert dataURL to File with timestamp to ensure unique filename
                    const timestamp = Date.now();
                    const filename = `edited_${timestamp}.jpg`;
                    const annotatedFile = dataURLtoFile(dataUrl, filename);

                    // Upload the new annotated image FIRST. We only remove the original after the
                    // replacement is safely stored — otherwise a failed upload would leave the
                    // deficiency with no evidence at all. On failure we keep the annotator open
                    // (the original is still intact) so the user can retry.
                    await onUpload([annotatedFile]);

                    // Replacement is stored — now delete the old image from the server.
                    if (onDeleteImage && deficiencyId) {
                        try {
                            await onDeleteImage(
                                editingImageInfo.deficiencyImageId,
                                deficiencyId,
                                imageType
                            );
                        } catch (deleteError) {
                            console.error('Error deleting old image:', deleteError);
                            // The replacement is already saved; leaving the old image is a benign
                            // duplicate (not data loss), so we don't fail the whole operation.
                        }
                    }

                    // Also mark for deletion locally (in case user doesn't save)
                    handleMarkForDeletion(editingImageInfo.deficiencyImageId);

                    // Clean up
                    setAnnotatorOpen(false);
                    setCurrentAnnotatingImageUrl(null);
                    setEditingImageInfo(null);
                    showNotification(`${imageType} image updated.`, 'success');
                } else {
                    // New image upload flow
                    if (!currentFileInfo) return;

                    // Saving without adding a mark uploads the original bytes untouched. When
                    // annotations exist, only the first image is replaced by the full-resolution
                    // JPEG export; the rest of a multi-select remain their original files.
                    const filesToUpload = hasAnnotations
                        ? [
                              dataURLtoFile(dataUrl, getJpegFilename(currentFileInfo.file.name)),
                              ...currentFileInfo.allFiles.slice(1),
                          ]
                        : currentFileInfo.allFiles;

                    await onUpload(filesToUpload);

                    // Clean up
                    URL.revokeObjectURL(currentAnnotatingImageUrl);
                    setAnnotatorOpen(false);
                    setCurrentAnnotatingImageUrl(null);
                    setCurrentFileInfo(null);
                    showNotification(`${imageType} image uploaded.`, 'success');
                }
            } catch (error) {
                console.error(`Error uploading annotated ${imageType} image:`, error);
                // Leave the annotator open with the photo so the user can simply press Save again
                // to retry — no page refresh or re-selection needed.
                showNotification(getUploadErrorMessage(error), 'error');
            } finally {
                setIsUploading(false);
            }
        };

        /**
         * Handle closing annotator without saving (skip annotation)
         */
        const handleAnnotatorClose = async () => {
            try {
                // If editing existing image, just close without changes
                if (editingImageInfo) {
                    setAnnotatorOpen(false);
                    setCurrentAnnotatingImageUrl(null);
                    setEditingImageInfo(null);
                    setIsUploading(false);
                    return;
                }

                if (!currentFileInfo) return;

                // Upload the original file(s) without annotation
                await onUpload(currentFileInfo.allFiles);

                // Clean up
                URL.revokeObjectURL(currentAnnotatingImageUrl);
                setAnnotatorOpen(false);
                setCurrentAnnotatingImageUrl(null);
                setCurrentFileInfo(null);
                showNotification(`${imageType} image uploaded.`, 'success');
            } catch (error) {
                console.error(`Error uploading ${imageType} images:`, error);
                // Keep the annotator open so the user can retry without re-selecting the photo.
                showNotification(getUploadErrorMessage(error), 'error');
            } finally {
                setIsUploading(false);
            }
        };

        /**
         * Handle opening annotator for editing an existing image
         * @param {Object} imageData - The image object to edit
         */
        const handleEditImage = async (imageData) => {
            try {
                setIsUploading(true);

                const imageUrl = getImageUrl(imageData.imageUrl);

                // Store the image info for later
                setEditingImageInfo({
                    deficiencyImageId: imageData.deficiencyImageId,
                    originalUrl: imageData.imageUrl,
                });

                // Open annotator with the existing image
                setCurrentAnnotatingImageUrl(imageUrl);
                setAnnotatorOpen(true);
            } catch (error) {
                console.error(`Error opening image editor:`, error);
                setIsUploading(false);
            }
        };

        /**
         * Marks an image for deletion in the UI without immediately deleting it from the API.
         * The actual deletion will be handled by the parent component when the user saves changes.
         *
         * @param {string} imageId - The ID of the image to mark for deletion
         */
        const handleMarkForDeletion = (imageId) => {
            // Add the image ID to the list of images to delete
            setImagesToDelete((prev) => [...prev, imageId]);

            // Update the parent component's state using onChange
            const newImages = images.filter(
                (img) =>
                    img.deficiencyImageId !== imageId &&
                    !imagesToDelete.includes(img.deficiencyImageId)
            );
            if (onChange) {
                onChange(newImages);
            }
        };

        /**
         * Exposes methods to the parent component through the forwarded ref.
         * This allows the parent to:
         * 1. Get the list of images marked for deletion (getImagesToDelete)
         * 2. Clear the deletion markers if needed, e.g. when canceling (clearDeleteMarkers)
         */
        useImperativeHandle(
            ref,
            () => ({
                /**
                 * Returns the array of image IDs that have been marked for deletion
                 * @returns {Array<string>} Array of image IDs to delete
                 */
                getImagesToDelete: () => imagesToDelete,

                /**
                 * Clears all deletion markers, used when canceling edits
                 */
                clearDeleteMarkers: () => setImagesToDelete([]),
            }),
            [imagesToDelete]
        );

        const handleImageClick = (index) => {
            setSelectedImageIndex(index);
        };

        const handleCloseModal = () => {
            setSelectedImageIndex(null);
        };

        return (
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: '#666666',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1.5,
                        display: 'block',
                    }}
                >
                    {imageType} Images {displayedImages ? `(${displayedImages.length})` : '(0)'}
                    {isUploading && (
                        <CircularProgress
                            size={12}
                            sx={{ ml: 1, verticalAlign: 'middle', color: '#1652F0' }}
                        />
                    )}
                </Typography>

                <Grid container spacing={1.5}>
                    {displayedImages && displayedImages.length > 0 ? (
                        // Show existing images in a grid
                        displayedImages.map((img, index) => {
                            const imageUrl = getImageUrl(img.imageUrl);

                            return (
                                <Grid item xs={4} key={img.deficiencyImageId} sx={{ mb: 1 }}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            paddingTop: '100%', // This creates a perfect square
                                            borderRadius: 1.5,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                            backgroundColor: '#f9f9f9',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            component="button"
                                            type="button"
                                            aria-label={`Open ${imageType.toLowerCase()} image ${index + 1} of ${displayedImages.length}`}
                                            onClick={() => handleImageClick(index)}
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                width: '100%',
                                                height: '100%',
                                                p: 0,
                                                border: 0,
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                '&:focus-visible': {
                                                    outline: '3px solid #1652F0',
                                                    outlineOffset: '-3px',
                                                },
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt=""
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => {
                                                    // Set fallback image when loading fails
                                                    e.target.src =
                                                        'https://via.placeholder.com/100x100?text=Image+Error';
                                                }}
                                            />
                                        </Box>

                                        {isEditing && (
                                            <>
                                                {/* Edit button */}
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditImage(img);
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        left: 4,
                                                        backgroundColor: 'rgba(25, 118, 210, 0.85)',
                                                        color: 'white',
                                                        padding: '6px',
                                                        width: 36,
                                                        height: 36,
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(25, 118, 210, 1)',
                                                        },
                                                    }}
                                                >
                                                    <EditIcon
                                                        fontSize="small"
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                </IconButton>

                                                {/* Delete button */}
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMarkForDeletion(
                                                            img.deficiencyImageId
                                                        );
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                                        color: 'white',
                                                        padding: '6px',
                                                        width: 36,
                                                        height: 36,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon
                                                        fontSize="small"
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })
                    ) : (
                        // Show placeholder when no images
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 100,
                                    backgroundColor: '#F9F9F9',
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px dashed #E0E0E0',
                                }}
                            >
                                <Typography variant="body2" color="#666666">
                                    No {imageType.toLowerCase()} images
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {/* Always show the Add image button unless readOnly */}
                    {!readOnly && (
                        <Grid item xs={4}>
                            <Box
                                component="label"
                                htmlFor={`upload-${imageType}`}
                                sx={{
                                    width: '100%',
                                    paddingTop: '100%', // Creates a perfect square
                                    backgroundColor: isUploading ? '#F0F0F0' : '#F5F8FF',
                                    borderRadius: 1.5,
                                    display: 'block',
                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                    position: 'relative',
                                    border: '1px dashed #1652F0',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#E5EEFF',
                                        border: '1px dashed #0039CB',
                                    },
                                    '&:focus-within': {
                                        outline: '2px solid #1652F0',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {isUploading ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: '#1652F0', mb: 1 }}
                                        />
                                    ) : (
                                        <AddIcon
                                            sx={{
                                                color: '#1652F0',
                                                fontSize: 24,
                                                mb: 1,
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#1652F0', fontSize: '0.7rem' }}
                                    >
                                        {isUploading ? 'Processing…' : `Add ${imageType}`}
                                    </Typography>
                                </Box>
                                <input
                                    type="file"
                                    id={`upload-${imageType}`}
                                    aria-label={`Add ${imageType} image`}
                                    accept="image/*,.heic,.HEIC,.heif,.HEIF"
                                    multiple
                                    onChange={handleImageUpload}
                                    // Keep the native input directly tappable. Some Android/Samsung
                                    // browsers do not activate a file input that is display:none when
                                    // its label is tapped. A transparent input preserves the custom UI
                                    // while allowing the browser to open its camera/library chooser.
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0,
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        zIndex: 1,
                                    }}
                                    disabled={isUploading || !onUpload}
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {selectedImageIndex !== null && (
                    <ImageCarouselModal
                        open
                        images={displayedImages.map((image) => getImageUrl(image.imageUrl))}
                        initialIndex={selectedImageIndex}
                        imageLabel={imageType}
                        onClose={handleCloseModal}
                    />
                )}

                {/* Upload feedback: validation errors, failures, and success confirmation */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={notification.severity === 'success' ? 3000 : 6000}
                    onClose={handleCloseNotification}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    sx={{ zIndex: 1400 }}
                >
                    <Alert
                        onClose={handleCloseNotification}
                        severity={notification.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>

                {/* Image Annotator Modal */}
                <ImageAnnotatorModal
                    open={annotatorOpen}
                    onClose={handleAnnotatorClose}
                    imageUrl={currentAnnotatingImageUrl}
                    onSave={handleAnnotatorSave}
                />
            </Box>
        );
    }
);

ImageGallery.displayName = 'ImageGallery';

ImageGallery.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            deficiencyImageId: PropTypes.string.isRequired,
            imageUrl: PropTypes.string.isRequired,
        })
    ),
    imageType: PropTypes.string.isRequired,
    isEditing: PropTypes.bool,
    onChange: PropTypes.func,
    onUpload: PropTypes.func,
    readOnly: PropTypes.bool,
    deficiencyId: PropTypes.string,
    onDeleteImage: PropTypes.func,
};

export default ImageGallery;
